import { useState } from 'react';
import { Grid } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import uniqueId from 'lodash.uniqueid';

import {
  LearnMoreAccordionContainer,
  LearnMoreAccordionSummary,
  LearnMoreAccordionDetails,
  LearnMoreTitle,
  LearnMoreDescription,
  RelevantLinksStack,
  RelevantLinksTitle,
  RelevantLinksButtonStack,
  RelevantLinkButton,
  ProjectsStack,
  ProjectsTitle,
  ProjectCard,
  ProjectCardHeader,
  ProjectTitle,
  ProjectSubtitle,
  ProjectCardContent,
  ProjectToolsStack,
  ProjectDetailsGrid,
  ProjectSectionTitle,
  ProjectDetailsList,
  ProjectDetailsListItem,
  ProjectDetailsText,
} from './ProfessionalBackground.styles';
import LanguageIcon from './LanguageIcon';

function LearnMoreAccordion({ learnMoreAbout, isMobile }) {
  const [expanded, setExpanded] = useState(false);

  const renderRelevantLinks = () => {
    if (learnMoreAbout.relevantLinks.length === 0) {
      return null;
    }
    return (
      <RelevantLinksStack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
      >
        <RelevantLinksTitle variant="h4">Relevant Links</RelevantLinksTitle>
        <RelevantLinksButtonStack direction={isMobile ? 'column' : 'row'}>
          {learnMoreAbout.relevantLinks.map(link => {
            return (
              <RelevantLinkButton
                key={uniqueId(link)}
                variant="contained"
                target="_blank"
                rel="noopener"
                href={link.url}
              >
                {link.name}
              </RelevantLinkButton>
            );
          })}
        </RelevantLinksButtonStack>
      </RelevantLinksStack>
    );
  };

  const renderProjects = () => {
    if (learnMoreAbout.projects.length === 0) {
      return null;
    }
    return (
      <ProjectsStack direction="column">
        <ProjectsTitle variant="h4">Projects</ProjectsTitle>
        <br />
        {learnMoreAbout.projects.map(project => {
          return (
            <ProjectCard key={project.projectTitle}>
              <ProjectCardHeader
                title={
                  <ProjectTitle variant="h5">
                    {project.projectTitle}
                  </ProjectTitle>
                }
                subheader={
                  <ProjectSubtitle variant="subtitle1">{`${project.participationLevel}`}</ProjectSubtitle>
                }
              />
              <ProjectCardContent>
                <ProjectToolsStack direction="row" justifyContent="center">
                  {project.toolsUsed.map(tool => {
                    return <LanguageIcon key={uniqueId(tool)} name={tool} />;
                  })}
                </ProjectToolsStack>
                <ProjectDetailsGrid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={12}>
                    <ProjectSectionTitle variant="h6" noWrap>
                      User Story:
                    </ProjectSectionTitle>
                    <ProjectDetailsList>
                      <ProjectDetailsListItem>
                        <ProjectDetailsText variant="body1">
                          {project.descriptionDetails.userStory}
                        </ProjectDetailsText>
                      </ProjectDetailsListItem>
                    </ProjectDetailsList>
                  </Grid>
                  <Grid item xs={12}>
                    <ProjectSectionTitle variant="h6" noWrap>
                      Acceptance Criteria:
                    </ProjectSectionTitle>
                    <ProjectDetailsList>
                      {project.descriptionDetails.ac.map(criterion => {
                        return (
                          <ProjectDetailsListItem key={uniqueId(criterion)}>
                            <ProjectDetailsText variant="body1">
                              {criterion}
                            </ProjectDetailsText>
                          </ProjectDetailsListItem>
                        );
                      })}
                    </ProjectDetailsList>
                  </Grid>
                  <Grid item xs={12}>
                    <ProjectSectionTitle variant="h6" noWrap>
                      Results:
                    </ProjectSectionTitle>
                    <ProjectDetailsList>
                      <ProjectDetailsListItem>
                        <ProjectDetailsText variant="body1">
                          {project.descriptionDetails.conclusion}
                        </ProjectDetailsText>
                      </ProjectDetailsListItem>
                    </ProjectDetailsList>
                  </Grid>
                </ProjectDetailsGrid>
              </ProjectCardContent>
            </ProjectCard>
          );
        })}
      </ProjectsStack>
    );
  };

  return (
    <LearnMoreAccordionContainer
      expanded={expanded}
      onChange={() => {
        setExpanded(!expanded);
      }}
    >
      <LearnMoreAccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        aria-expanded={expanded}
      >
        <LearnMoreTitle variant="subtitle2">Learn More</LearnMoreTitle>
        <LearnMoreDescription>
          View projects, relevant links, and other specifics.
        </LearnMoreDescription>
      </LearnMoreAccordionSummary>
      <LearnMoreAccordionDetails
        id="panel1bh-content"
        role="region"
        aria-labelledby="panel1bh-header"
      >
        {renderRelevantLinks()}
        {renderProjects()}
      </LearnMoreAccordionDetails>
    </LearnMoreAccordionContainer>
  );
}

export default LearnMoreAccordion;
